/**
 * Convite e "Por onde começamos?" (marco 1), na moldura do onboarding (protótipo do Claude
 * Design, 28/10/2026). O convite entra no lugar de "Sua primeira busca" no onboarding "novo": "Sim,
 * me mostre" troca o conteúdo no lugar pela escolha da busca; "Agora não" (e Esc, ✕ ou clique fora)
 * fecha e mostra onde o guia fica.
 *
 * A escolha mostra as 3 buscas do perfil (as mesmas de "Sua primeira busca"). Aberto pelo menu ou
 * pelo Início, o perfil vem da conta; sem perfil, mostra os três tipos de busca explicados.
 * "Começar por esta" preenche a busca e abre a janela de captura; "Quero buscar outra coisa" abre
 * o menu do "Capturar Leads".
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../../hooks/useBrand';
import { usePresenca } from '../../hooks/usePresenca';
import { assetUrls } from '../../config/env';
import { unwrap } from '../../lib/apiClient';
import { emitActivity } from '../../lib/activity';
import { accountOverviewKey, fetchAccountOverview } from '../../api/account';
import { useSessionStore } from '../../stores/sessionStore';
import { useCaptureStore } from '../../stores/captureStore';
import { cityLabel, userPlace } from '../onboarding/place';
import { useOnboardingText } from '../onboarding/onboardingText';
import {
  buildSuggestions,
  fillCaptureForm,
  placeWords,
  profileSummary,
} from '../onboarding/primeiraBusca';
import { useGuiaStore } from './guiaStore';
import { abrirBuscaDoGuia, comecarGuia } from './guiaAcoes';
import { useGuiaText, type GuiaKey } from './guiaText';
import type { GuiaModulo } from './passos';
import { perfilDaConta } from './perfil';
import '../onboarding/onboarding.css';
import './guia.css';

const KIND_ICON = { loc: 'storefront', tel: 'call', mail: 'mail' } as const;
const KIND_LABEL: Record<'loc' | 'tel' | 'mail', GuiaKey> = {
  loc: 'k_loc',
  tel: 'k_tel',
  mail: 'k_mail',
};
const TIPOS: Array<{
  modulo: GuiaModulo;
  icon: string;
  texto: GuiaKey;
  titulo: string;
  tituloPadrao: string;
}> = [
  {
    modulo: 'places',
    icon: 'storefront',
    texto: 't_places',
    titulo: 'home3b_capture_places_title',
    tituloPadrao: 'Telefones de negócios locais',
  },
  {
    modulo: 'phones',
    icon: 'call',
    texto: 't_phones',
    titulo: 'home3b_capture_phones_title',
    tituloPadrao: 'Telefones de empresas ou profissionais',
  },
  {
    modulo: 'emails',
    icon: 'mail',
    texto: 't_emails',
    titulo: 'home3b_capture_emails_title',
    tituloPadrao: 'Emails de empresas ou profissionais',
  },
];
const MARCOS: GuiaKey[] = ['m1', 'm2', 'm3', 'm4'];
const REDE: Record<string, string> = { instagram: 'Instagram', linkedin: 'LinkedIn' };

export function GuiaJanela(): JSX.Element | null {
  const janela = useGuiaStore((s) => s.janela);
  const { montado, aberto } = usePresenca(janela !== null, '--duracao-200');
  // A última janela aberta continua desenhada enquanto sai.
  const [ultima, setUltima] = useState(janela);
  if (janela && janela !== ultima) setUltima(janela);
  if (!montado || !ultima) return null;
  return <Conteudo modo={ultima} aberto={aberto} />;
}

function Conteudo({
  modo,
  aberto,
}: {
  modo: 'convite' | 'escolher';
  aberto: boolean;
}): JSX.Element {
  const { tx, lang } = useGuiaText();
  const ob = useOnboardingText();
  const { t } = useTranslation();
  const brand = useBrand();
  const navigate = useNavigate();
  const session = useSessionStore((s) => s.session);
  const perfilDoConvite = useGuiaStore((s) => s.perfil);
  const place = useMemo(() => userPlace(), []);
  const words = useMemo(() => placeWords(ob.tx, ob.lang), [ob.tx, ob.lang]);
  const [picked, setPicked] = useState(0);
  const [busy, setBusy] = useState(false);
  const respondeu = useRef(false);

  // Aberto pelo menu ou pelo Início: o perfil da conta (o mesmo de Perfil e preferências).
  const overview = useQuery({
    queryKey: accountOverviewKey(session?.userId ?? ''),
    queryFn: async () => unwrap(await fetchAccountOverview()),
    enabled: modo === 'escolher' && !perfilDoConvite && Boolean(session?.userId),
    staleTime: 60 * 1000,
  });
  const perfil =
    perfilDoConvite ?? perfilDaConta(overview.data?.profile, (segment) => ob.segmentName(segment));
  const carregando = modo === 'escolher' && !perfilDoConvite && overview.isLoading;
  const items = perfil ? buildSuggestions(perfil, place, words, ob.suggestion) : [];

  useEffect(() => {
    if (modo === 'convite') emitActivity('guide.invited', { source: 'onboarding' });
  }, [modo]);

  const fechar = (): void => {
    if (modo === 'convite') {
      if (!respondeu.current) emitActivity('guide.answered', { answer: 'dismissed' });
      respondeu.current = true;
      useGuiaStore.getState().recusarConvite();
    } else {
      useGuiaStore.getState().fecharJanela();
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') fechar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo]);

  async function comecarPor(): Promise<void> {
    if (busy) return;
    if (items.length > 0) {
      const item = items[picked];
      if (!item) return;
      setBusy(true);
      await fillCaptureForm(item.form, place, lang);
      setBusy(false);
      useGuiaStore.getState().fecharJanela();
      abrirBuscaDoGuia(item.form.module as GuiaModulo, {
        termo: ob.suggestion(item.id, 'kw'),
        rede: item.def.network ? (REDE[item.def.network] ?? '') : '',
      });
      return;
    }
    const tipo = TIPOS[picked];
    if (!tipo) return;
    useGuiaStore.getState().fecharJanela();
    abrirBuscaDoGuia(tipo.modulo, { termo: '', rede: '' });
  }

  function outraCoisa(): void {
    useGuiaStore.getState().fecharJanela();
    useGuiaStore.getState().ir('tipo', { modulo: null, termo: '', rede: '' });
    useCaptureStore.getState().openCategories();
  }

  const city = cityLabel(place);
  const marcoAtual = modo === 'escolher' ? 1 : 0;

  return createPortal(
    <div
      className={`ob-overlay gp-janela ds-veu${aberto ? ' is-aberto' : ''}`}
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && fechar()}
    >
      <div
        className={`ob-dialog ds-dialogo ds-grande${aberto ? ' is-aberto' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gp-janela-titulo"
      >
        <div className="ob-head">
          <div className="ob-head__row">
            <span className="ob-brand">
              <img src={assetUrls.logoLeadMachine} alt="" width={28} height={28} />
              <strong>{brand.appTitle}</strong>
            </span>
            {modo === 'escolher' ? (
              <span className="ob-step">
                <strong>{tx('step_of', { n: 1, name: tx('m1') })}</strong>
              </span>
            ) : null}
          </div>
          {modo === 'escolher' ? (
            <div className="ob-bars" aria-hidden="true">
              {MARCOS.map((m, i) => (
                <span key={m} className="ds-progresso">
                  <span style={{ transform: `scaleX(${i < marcoAtual ? 1 : 0})` }} />
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="ob-body">
          {modo === 'convite' ? (
            <section className="gp-convite ds-aparece" key="convite">
              <h2 id="gp-janela-titulo">{tx('conv_title')}</h2>
              <p>{city ? tx('conv_text_where', { city }) : tx('conv_text')}</p>
              <div className="gp-plano">
                <span>{tx('conv_plan')}</span>
                <ol>
                  {MARCOS.map((m, i) => (
                    <li key={m}>
                      <span aria-hidden="true">{i + 1}</span>
                      <span>
                        <strong>{tx(m)}</strong>
                        <small>{tx(`${m}_sub` as GuiaKey)}</small>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            </section>
          ) : carregando ? (
            <section className="gp-escolha" key="carregando" aria-busy="true">
              <h2 id="gp-janela-titulo">{tx('esc_title')}</h2>
            </section>
          ) : items.length > 0 && perfil ? (
            <section className="gp-escolha ds-aparece" key="escolha">
              <h2 id="gp-janela-titulo">{tx('esc_title')}</h2>
              <p>{tx('esc_text', { profile: profileSummary(perfil, place, words, ob.tx) })}</p>
              <div className="fs-list" role="radiogroup" aria-labelledby="gp-janela-titulo">
                {items.map((item, index) => (
                  <label
                    key={item.id}
                    className={`fs-item ds-escolhivel ds-entra ds-escalonado${picked === index ? ' is-escolhido' : ''}`}
                    style={{ '--i': index } as CSSProperties}
                  >
                    <input
                      type="radio"
                      name="gp-busca"
                      checked={picked === index}
                      onChange={() => setPicked(index)}
                    />
                    <span className="fs-item__body">
                      <span className="fs-item__tags">
                        <span className={`fs-kind fs-kind--${item.def.kind}`}>
                          <span className="material-symbols-rounded" aria-hidden="true">
                            {KIND_ICON[item.def.kind]}
                          </span>
                          {tx(KIND_LABEL[item.def.kind])}
                        </span>
                        {index === 0 ? (
                          <span className="fs-rec">
                            <span className="material-symbols-rounded" aria-hidden="true">
                              star
                            </span>
                            {ob.tx('recommended')}
                          </span>
                        ) : null}
                      </span>
                      <span className="fs-item__title">
                        <strong>{item.term}</strong> {item.where}
                      </span>
                      <span className="fs-item__why">{item.why}</span>
                    </span>
                  </label>
                ))}
              </div>
              {city ? (
                <p className="fs-note">
                  <span className="material-symbols-rounded" aria-hidden="true">
                    location_on
                  </span>
                  {tx('esc_note', { city })}
                </p>
              ) : null}
            </section>
          ) : (
            <section className="gp-escolha ds-aparece" key="tipos">
              <h2 id="gp-janela-titulo">{tx('esc_types_title')}</h2>
              <p>{tx('esc_types_text')}</p>
              <div className="fs-list" role="radiogroup" aria-labelledby="gp-janela-titulo">
                {TIPOS.map((tipo, index) => (
                  <label
                    key={tipo.modulo}
                    className={`fs-item ds-escolhivel ds-entra ds-escalonado${picked === index ? ' is-escolhido' : ''}`}
                    style={{ '--i': index } as CSSProperties}
                  >
                    <input
                      type="radio"
                      name="gp-tipo"
                      checked={picked === index}
                      onChange={() => setPicked(index)}
                    />
                    <span className="fs-item__body">
                      <span className="fs-item__title">
                        <strong>{t(tipo.titulo, tipo.tituloPadrao)}</strong>
                      </span>
                      <span className="fs-item__why">{tx(tipo.texto)}</span>
                    </span>
                  </label>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="ob-foot">
          {modo === 'convite' ? (
            <button
              type="button"
              className="gp-agora ds-resposta"
              onClick={() => {
                respondeu.current = true;
                emitActivity('guide.answered', { answer: 'no' });
                useGuiaStore.getState().recusarConvite();
              }}
            >
              {tx('conv_no')}
            </button>
          ) : items.length > 0 ? (
            <button type="button" className="ob-link ds-resposta" onClick={outraCoisa}>
              {tx('esc_other')}
            </button>
          ) : (
            <button
              type="button"
              className="ob-link ds-resposta"
              onClick={() => {
                useGuiaStore.getState().fecharJanela();
                navigate('/account/preferences');
              }}
            >
              {tx('esc_profile_link')}
            </button>
          )}
          <div className="ob-foot__go">
            {modo === 'convite' ? (
              <button
                type="button"
                className="ob-primary ds-resposta"
                onClick={() => {
                  respondeu.current = true;
                  emitActivity('guide.answered', { answer: 'yes' });
                  comecarGuia('convite', perfilDoConvite);
                }}
              >
                {tx('conv_yes')}
                <span className="material-symbols-rounded" aria-hidden="true">
                  arrow_forward
                </span>
              </button>
            ) : (
              <button
                type="button"
                className="ob-primary ds-resposta"
                aria-disabled={carregando || busy || undefined}
                onClick={() => {
                  if (!carregando) void comecarPor();
                }}
              >
                {tx('esc_go')}
                <span className="material-symbols-rounded" aria-hidden="true">
                  arrow_forward
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
